// ==========================================================================
// SignalFix v2 — Map legend (bottom-anchored)
// --------------------------------------------------------------------------
// Anchored to the bottom-left: collapsed it sits at the bottom edge, and it
// expands upward when opened. Shows:
//   1. the road-surface-condition gradient of the vehicle path (Ottimo->Critico)
//   2. the detected elements grouped by family (point / segmented)
//   3. a high-level category filter (extra) that toggles element families
// All content is driven by the shared SIGNAL_ELEMENTS / CATEGORY_FILTERS.
// ==========================================================================

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "antd";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { SIGNAL_ELEMENTS, LEGEND_GROUPS, CATEGORY_FILTERS } from "../data/signalElements";

// Small swatch for a segmented element (a short line in its stroke style).
const StrokeSwatch = ({ stroke }) => (
  <svg width="26" height="16" viewBox="0 0 26 16" style={{ display: "block" }}>
    <line
      x1="1"
      y1="8"
      x2="25"
      y2="8"
      stroke={stroke.color}
      strokeWidth={Math.min(stroke.weight, 8)}
      strokeDasharray={stroke.dashArray || undefined}
      strokeLinecap="round"
    />
  </svg>
);

const LegendIcon = ({ element }) => {
  if (element.stroke) return <StrokeSwatch stroke={element.stroke} />;
  if (element.iconSvg) {
    return (
      <span
        className="signalfix-legend-icon"
        // Inline SVG string from the registry.
        dangerouslySetInnerHTML={{ __html: element.iconSvg(22) }}
      />
    );
  }
  return <span className="signalfix-legend-icon" />;
};

const SignalFixLegend = ({ categories, onToggleCategory }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  return (
    <div className={`signalfix-legend ${open ? "is-open" : "is-collapsed"}`}>
      {/* Body first in the DOM so the header stays pinned at the bottom and
          the panel grows upward when expanded. */}
      {open && (
        <div className="signalfix-legend-body">
          {/* Road-surface condition gradient */}
          <div className="signalfix-legend-section-title">
            {t("signalFix.legend.roadCondition", "Stato manto stradale (percorso veicolo)")}
          </div>
          <div className="signalfix-legend-gradient-bar" />
          <div className="signalfix-legend-gradient-labels">
            <span>{t("signalFix.legend.optimal", "Ottimo")}</span>
            <span>{t("signalFix.legend.good", "Buono")}</span>
            <span>{t("signalFix.legend.moderate", "Moderato")}</span>
            <span>{t("signalFix.legend.critical", "Critico")}</span>
          </div>

          {/* Detected elements, grouped */}
          {LEGEND_GROUPS.map((group) => (
            <div key={group.key} className="signalfix-legend-group">
              <div className="signalfix-legend-section-title">
                {t(
                  group.titleKey,
                  group.key === "bbox" ? "Elementi puntuali (bbox)" : "Elementi segmentati (seg)"
                )}
              </div>
              {group.items.map((key) => {
                const element = SIGNAL_ELEMENTS[key];
                if (!element) return null;
                return (
                  <div key={key} className="signalfix-legend-item">
                    <LegendIcon element={element} />
                    <span className="signalfix-legend-label">
                      {t(`signalFix.elements.${key}`, element.label)}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Category filter (extra) — toggles element families on the map */}
          {categories && onToggleCategory && (
            <div className="signalfix-legend-group">
              <div className="signalfix-legend-section-title">
                {t("signalFix.filterTitle", "Filtri")}
              </div>
              {CATEGORY_FILTERS.map((cat) => (
                <div key={cat.key} className="signalfix-legend-filter-item">
                  <span
                    className="signalfix-legend-label"
                    style={{ fontWeight: cat.master ? 700 : 400 }}
                  >
                    {t(cat.labelKey)}
                  </span>
                  <Switch
                    size="small"
                    checked={!!categories[cat.key]}
                    onChange={(checked) => onToggleCategory(cat.key, checked)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button className="signalfix-legend-header" onClick={() => setOpen((v) => !v)} type="button">
        <span>{t("signalFix.legend.title", "Legenda")}</span>
        {open ? <FiChevronDown /> : <FiChevronUp />}
      </button>
    </div>
  );
};

export default SignalFixLegend;
